import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  BonusesIcon,
  InjectedModalProps,
  Modal,
  SafeIcon,
  SvgProps,
  TeamIcon,
  Text,
  TradesIcon,
  TradingIcon,
} from '@nominex/dex-uikit'
import { SlButton } from '@shoelace-style/shoelace/dist/react'
import { bonusesApi, infoApi } from 'restApi'
import { useTranslation } from 'contexts/Localization'
import { useNmxUsdtPrice } from 'hooks/useUsdtPrice'
import useToast from 'hooks/useToast'
import useRestData from 'hooks/useRestData'
import { AppState, useAppDispatch } from 'state'
import { fetchUserBonuses, fetchUserBonusesTotalEarned } from 'state/affiliate'
import { FetchStatus } from 'state/types'
import LoadableValue from 'components/LoadableValue'
import { SoonTag } from '../Tags'
import SuccessfulClaimOverlay from './SuccessfulClaimOverlay'

enum RowVariant {
  CASHBACK = 'CASHBACK',
  HOLDER_BONUS = 'HOLDER_BONUS',
  REFERRAL_FARMING = 'REFERRAL_FARMING',
  REFERRAL_TRADING = 'REFERRAL_TRADING',
  TEAM_FARMING = 'TEAM_FARMING',
  TEAM_TRADING = 'TEAM_TRADING',
  TRADING_FEES = 'TRADING_FEES',
}

type RowProps = {
  available?: boolean
  fetchStatus?: FetchStatus
  label: string
  value: number
  variant: RowVariant
}

const RowIcon: React.FC<SvgProps & { variant: RowVariant }> = ({ variant, ...props }) => {
  switch (variant) {
    case RowVariant.CASHBACK:
      return <BonusesIcon color="indigo500" {...props} />
    case RowVariant.HOLDER_BONUS:
      return <SafeIcon color="purple500" {...props} />
    case RowVariant.REFERRAL_TRADING:
    case RowVariant.TEAM_TRADING:
      return <TradesIcon color="aqua500" {...props} />
    case RowVariant.REFERRAL_FARMING:
    case RowVariant.TEAM_FARMING:
      return <TeamIcon color="pink500" {...props} />
    case RowVariant.TRADING_FEES:
      return <TradingIcon color="indigo500" {...props} />
    default:
      throw new Error('Unknown row type')
  }
}

const Row: React.FC<RowProps> = ({ available, fetchStatus, label, variant, value }) => (
  <div className="flex py-12">
    <RowIcon variant={variant} height="24px" width="24px" mr="8px" />

    <div className="flex items-center justify-between grow">
      <Text fontSize="14px" color="white">
        {label}
      </Text>
      {available ? (
        <LoadableValue
          loading={fetchStatus !== FetchStatus.FETCHED}
          fontSize="16px"
          color="white"
          height="24px"
          width="60px"
          bold
        >
          {value.toFixed(3)} NMX
        </LoadableValue>
      ) : (
        <SoonTag />
      )}
    </div>
  </div>
)

const ClaimModal: React.FC<InjectedModalProps> = ({ onDismiss }) => {
  const { t } = useTranslation()
  const { toastError } = useToast()

  const {
    fetchStatus: bonusesFetchStatus,
    cashback,
    holderBonus,
    referralFarming,
    referralTrading,
    teamFarming,
    teamTrading,
    total,
  } = useSelector((state: AppState) => state.affiliate.user.bonuses)

  const [feeFetchStatus, { claimFee }] = useRestData(infoApi.getFees, { claimFee: 0 })

  const nmxPrice = Number((useNmxUsdtPrice() || 0).toFixed(8))

  const totalUsd = total * nmxPrice
  const feeUsd = claimFee * nmxPrice

  const [isClaimed, setIsClaimed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchUserBonuses())
    dispatch(fetchUserBonusesTotalEarned())
  }, [dispatch, isClaimed])

  const claimBonuses = async () => {
    setIsLoading(true)
    try {
      await bonusesApi.claim({
        claimRequest: {
          claimPersonal: true,
          claimTeam: true,
        },
      })
      setIsClaimed(true)
      dispatch(fetchUserBonuses())
      dispatch(fetchUserBonusesTotalEarned())
    } catch (error: any) {
      const [{ code }] = await error.json()
      switch (code) {
        case '05.00.02.01':
          toastError(t('Error'), t('Claiming currently unavailable.'))
          break
        case '05.00.02.02':
          toastError(t('Error'), t('Your unclaimed balance too low.'))
          break
        default:
          toastError(t('Error'), t('Can’t claim rewards. Please try again or contact support if the problem persists.'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      bodyPadding="0"
      className="max-w-[480px] relative"
      title={t('Claim rewards')}
      onDismiss={() => {
        onDismiss()
        setIsClaimed(false)
      }}
    >
      {isClaimed && <SuccessfulClaimOverlay onDismiss={onDismiss} />}
      <div className="flex flex-col py-12 px-24">
        <Text fontSize="14px" textAlign="justify" color="darkGray100">
          {t(
            'Total amount of NMX extra rewards available for you to claim. We advise you to only claim NMX rewards when you have earned enough to cover the network fee.',
          )}
        </Text>
        <Row
          variant={RowVariant.CASHBACK}
          label={t('Trading fees cashback')}
          value={cashback}
          fetchStatus={bonusesFetchStatus}
          available
        />
        <Row
          variant={RowVariant.HOLDER_BONUS}
          label={t('Holder bonus')}
          value={holderBonus}
          fetchStatus={bonusesFetchStatus}
          available
        />
        <Row
          variant={RowVariant.REFERRAL_FARMING}
          label={t('Referral farming')}
          value={referralFarming}
          fetchStatus={bonusesFetchStatus}
          available
        />
        <Row
          variant={RowVariant.TEAM_FARMING}
          label={t('Team farming')}
          value={teamFarming}
          fetchStatus={bonusesFetchStatus}
          available
        />
        <Row
          variant={RowVariant.REFERRAL_TRADING}
          label={t('Referral trading')}
          value={referralTrading}
          fetchStatus={bonusesFetchStatus}
          available
        />
        <Row
          variant={RowVariant.TEAM_TRADING}
          label={t('Team trading')}
          value={teamTrading}
          fetchStatus={bonusesFetchStatus}
          available
        />
      </div>

      <div className="flex flex-col p-24 bg-dark-gray-600">
        <div className="flex items-center justify-between mb-12">
          <Text>{t('Total')}</Text>

          <div className="flex flex-col items-end">
            <Text fontSize="18px" color="green500" bold noTranslate>
              {total.toFixed(8)} NMX
            </Text>
            <LoadableValue loading={!nmxPrice} height="20px" fontSize="10px" color="darkGray100">
              ≈ ${totalUsd.toFixed(8)}
            </LoadableValue>
          </div>
        </div>

        <div className="flex items-center justify-between mb-12">
          <Text fontSize="16px" color="darkGray100" semibold>
            {t('Fee')}
          </Text>

          <div className="flex flex-col items-end">
            <LoadableValue
              loading={feeFetchStatus !== FetchStatus.FETCHED}
              height="24px"
              fontSize="16px"
              color="darkGray100"
            >
              {claimFee.toFixed(8)} NMX
            </LoadableValue>

            <LoadableValue loading={!nmxPrice} height="20px" fontSize="10px" color="darkGray100">
              ≈ ${feeUsd.toFixed(8)}
            </LoadableValue>
          </div>
        </div>

        <SlButton variant="success" loading={isLoading} onClick={claimBonuses}>
          {t('Claim')}
        </SlButton>
      </div>
    </Modal>
  )
}

export default ClaimModal
